import { useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "@emotion/styled";
import {
  SegPatenteInput,
  normalizarPatente,
} from "../../../Components/SegUI/SegPatenteInput/SegPatenteInput";
import { RX_PATENTE } from "../../../Components/Slides/Comunes/Cotizacion/validaciones";
import { useCotizacionStore } from "../../../Services/Providers/CotizacionStore";
import { getInicioCotizacion, getPasoDatos } from "../../../utils/getRubro";
import { quitarMarcaDeVendedor } from "../../../Services/ventaPresencial/marcaVendedor";
import { getWhatsappUrl, WHATSAPP_VERDE } from "../../../utils/whatsapp";

/** El naranja de acento de la marca, el mismo del hero y de los precios. */
const NARANJA = "#F59B00";
const NARANJA_HOVER = "#dd8a00";

const Card = styled.div`
  max-width: 380px;
  margin-left: auto;
  margin-right: auto;

  @media (min-width: 992px) {
    margin-right: 0;
  }
`;

const Rubro = styled.p`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--bs-primary);
  margin: 0 0 6px;
`;

const Etiqueta = styled.label`
  display: block;
  font-size: 15.5px;
  font-weight: 700;
  color: #10151f;
  margin: 0 0 12px;
`;

const BotonCotizar = styled.button`
  display: block;
  width: 100%;
  background: ${NARANJA};
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  border: 0;
  border-radius: 10px;
  padding: 14px 20px;
  transition: background 0.15s;

  &:hover,
  &:focus-visible {
    background: ${NARANJA_HOVER};
    color: #fff;
  }
`;

const Alternativa = styled.p`
  font-size: 13px;
  color: #7a8394;
  text-align: center;
  margin: 14px 0 0;

  button {
    background: none;
    border: 0;
    padding: 0;
    color: var(--bs-primary);
    font-size: inherit;
    font-weight: 500;
    text-decoration: underline;

    &:hover,
    &:focus-visible {
      color: var(--bs-link-hover-color);
    }
  }
`;

/** Título de la card UY: centrado, en el gris de acento de la marca. */
const TituloUY = styled.p`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: #7a8394;
  text-align: center;
  margin: 0 0 12px;
`;

/** Botón verde de WhatsApp: mismo alto que el CTA principal, va debajo. */
const BotonWhatsapp = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  background: ${WHATSAPP_VERDE};
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.02em;
  border-radius: 10px;
  padding: 13px 20px;
  margin-top: 10px;
  text-decoration: none;
  transition: background 0.15s;

  &:hover,
  &:focus-visible {
    background: #1ebe5d;
    color: #fff;
  }

  svg {
    width: 22px;
    height: 22px;
    display: block;
    flex: none;
  }
`;

/**
 * CTA de WhatsApp con el glyph oficial de marca (recoloreado en blanco para
 * que se lea sobre el verde). Abre el chat con el número del país.
 */
const CotizarPorWhatsapp = ({ pais }: { pais?: string }) => (
  <BotonWhatsapp href={getWhatsappUrl(pais)} target="_blank" rel="noreferrer">
    <svg viewBox="0 0 720 720" aria-hidden="true">
      <path
        fill="#fff"
        d="M360,0C161.18,0,0,161.18,0,360c0,65.41,17.45,126.75,47.94,179.61L0,720l187.02-44.21c51.34,28.18,110.28,44.21,172.98,44.21,198.82,0,360-161.18,360-360S558.82,0,360,0ZM360,655.52c-60.17,0-116.13-17.98-162.82-48.87l-110.49,28.14,30.99-105.61c-33.53-47.93-53.2-106.26-53.2-169.19,0-163.21,132.31-295.52,295.52-295.52s295.52,132.31,295.52,295.52-132.31,295.52-295.52,295.52Z"
      />
      <path
        fill="#fff"
        d="M444.35,407.52l87.1,41.06c4,1.88,6.56,5.94,6.2,10.34-.94,11.46-5.54,34.43-26.13,55.02-58.12,58.12-162.49-7.64-166.74-10.18-25.67-13.79-50.06-32.24-73.19-55.36-23.12-23.12-41.58-47.52-55.37-73.19-2.55-4.24-68.31-108.61-10.18-166.74,20.59-20.59,43.56-25.19,55.02-26.13,4.41-.36,8.46,2.2,10.34,6.2l41.07,87.1c1.94,4.12,1.09,9.02-2.13,12.24l-30.61,30.61c-6.62,6.62-8.56,16.93-4,25.11,11.17,20.03,26.19,39.32,43.59,57.07,17.75,17.4,37.04,32.43,57.07,43.59,8.18,4.56,18.48,2.62,25.11-4l30.61-30.61c3.22-3.22,8.12-4.08,12.24-2.13Z"
      />
    </svg>
    Cotizá por WhatsApp
  </BotonWhatsapp>
);

/** Lo que haya quedado de una cotización anterior no sirve para un auto nuevo. */
const SIN_VEHICULO = {
  marca: undefined,
  modelo: undefined,
  version: undefined,
  año: undefined,
  esAntiguo: undefined,
};

interface CotizadorPatenteCardProps {
  /** País del origen. En UY no hay datacar, así que no se pide patente. */
  pais?: string;
}

/**
 * Card del hero para arrancar a cotizar el auto desde la home.
 *
 * - AR: entra por la patente (chapa) — la patente queda en el store y se navega
 *   al paso 1 con ?buscar=1 para que el datacar se dispare solo. La consulta al
 *   datacar y su reCAPTCHA viven en un solo lugar (InicioNormal).
 * - UY: no hay datacar, así que no se pide patente; el CTA lleva directo al paso
 *   de datos (se elige el vehículo) y se ofrece ayuda por WhatsApp.
 */
export const CotizadorPatenteCard = ({ pais }: CotizadorPatenteCardProps) => {
  const nav = useNavigate();
  const { cotizacionValues, setCotizacionValues } = useCotizacionStore();

  const [patente, setPatente] = useState(
    cotizacionValues?.patente ? normalizarPatente(cotizacionValues.patente) : ""
  );
  const [error, setError] = useState("");

  /**
   * Sin patente el datacar no puede resolver nada: se va derecho al paso de
   * datos, donde el vehículo se elige por marca y modelo. (En UY es el único
   * camino; en AR es la salida "por marca".)
   */
  const cotizarSinPatente = () => {
    // No se borra el vehículo: si venía cargado (p. ej. se vuelve desde la home),
    // se conserva y reaparece en el paso de datos. Solo se limpia la patente para
    // entrar en modo manual (elegir por marca/modelo).
    // Entrada pública (US 23866): si la pestaña era de una cotización del asesor, pasa a ser de cliente.
    setCotizacionValues({
      ...quitarMarcaDeVendedor(cotizacionValues, window.location.href),
      patente: undefined,
    });
    nav(getPasoDatos("auto"));
  };

  // Uruguay: sin patente. Card simple + ayuda por WhatsApp.
  if (pais === "UY") {
    return (
      <Card className="bg-white rounded-18 shadow-4 px-3 px-lg-4 py-4">
        <TituloUY>Ahorrá en el seguro de tu auto</TituloUY>
        <BotonCotizar type="button" onClick={cotizarSinPatente}>
          Cotizar
        </BotonCotizar>
        <CotizarPorWhatsapp pais={pais} />
      </Card>
    );
  }

  const cotizar = (e: React.FormEvent) => {
    e.preventDefault();
    if (!patente) {
      setError("Ingresá la patente");
      return;
    }
    if (!RX_PATENTE.test(patente)) {
      setError("Patente inválida");
      return;
    }
    setError("");
    setCotizacionValues({
      ...cotizacionValues,
      ...SIN_VEHICULO,
      patente,
    });
    nav(`${getInicioCotizacion("auto")}?buscar=1`);
  };

  return (
    <Card className="bg-white rounded-18 shadow-4 px-3 px-lg-4 py-4">
      <form onSubmit={cotizar} noValidate>
        <Rubro>Seguro de auto</Rubro>
        <Etiqueta htmlFor="patente-home">Ingresá tu patente</Etiqueta>

        <SegPatenteInput
          value={patente}
          onChange={(valor) => {
            setPatente(valor);
            if (error) setError("");
          }}
          leyendaError={error}
          name="patente-home"
        />

        <BotonCotizar type="submit">Cotizar</BotonCotizar>

        <CotizarPorWhatsapp pais={pais} />

        <Alternativa>
          ¿No tenés la patente?{" "}
          <button type="button" onClick={cotizarSinPatente}>
            Cotizá por marca
          </button>
        </Alternativa>
      </form>
    </Card>
  );
};
